import { HttpErrorResponse } from '@angular/common/http';
import type * as echarts from 'echarts';

import { buildWaterfallTileData } from '../components/waterfall-step/waterfall-tile.utils';
import { normalizeDropdownOptionOrderMode, orderDropdownOptions } from '../components/dropdown-step/dropdown-options.utils';
import { stripWidgetData, toSerializable } from '../shared/self-service-json.utils';
import { restoreApiCallConfig } from './dvm-input-api-call.utils';
import type { DashboardTile, PublishedLayoutSnapshotItem, WaterfallBar } from './dvm-layout-builder.types';
import { effectiveListenerTileIds, effectiveTileFilterKey, isCrossFilterTile, isRecomputeCrossFilterTile } from './dvm-tile-crossfilter.utils';

/**
 * The dashboard's `layout_json` in both directions — pure functions:
 *  - restore: published widget → builder tile (`tileFromWidget`) and the saved
 *    grid placement (`applyPublishedLayoutToTiles`);
 *  - export: builder tiles → `pageContent` rows of widgets + the grid
 *    snapshot (`buildDashboardLayoutJson`), plus the publish envelope.
 * The shell supplies the little state these need through small contexts.
 */

// ── grid constants ──────────────────────────────────────────────────────────
export const DASH_COLS = 12;
export const DASH_ROW_PX = 40;
export const DASH_GAP = 12;

/** Published widget `type` → builder layout type. */
export const WIDGET_TYPE_TO_LAYOUT: Record<string, string> = {
  'waterfall-chart': 'WATERFALL',
  'drill-down': 'DRILL_DOWN_TABLE',
  'horizontal-bar-chart': 'HORIZONTAL_BAR_CHART',
  'kpi-card': 'KPI',
  'bar-column': 'BAR',
  'scatter-bubble': 'SCATTER_BUBBLE',
  'line-area': 'LINE_AREA',
  'slider': 'SLIDER',
  'input-box': 'INPUT_BOX',
  'dropdown': 'DROPDOWN',
  'table-matrix': 'TABLE_MATRIX',
  'file-upload': 'FILE_UPLOAD',
  'popup': 'POPUP',
  'bar-chart': 'BAR',
};

/** Builder layout type → published widget `type`. */
export const LAYOUT_TYPE_TO_WIDGET: Record<string, string> = {
  WATERFALL: 'waterfall-chart',
  DRILL_DOWN_TABLE: 'drill-down',
  HORIZONTAL_BAR_CHART: 'horizontal-bar-chart',
  KPI: 'kpi-card',
  BAR: 'bar-column',
  SCATTER_BUBBLE: 'scatter-bubble',
  LINE_AREA: 'line-area',
  SLIDER: 'slider',
  INPUT_BOX: 'input-box',
  DROPDOWN: 'dropdown',
  TABLE_MATRIX: 'table-matrix',
  FILE_UPLOAD: 'file-upload',
  POPUP: 'popup',
};

// ── small generic helpers ───────────────────────────────────────────────────
export function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function toFiniteNumber(value: unknown): number | null {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/** A serialisable copy of a plain object (arrays / primitives → null). */
export function cloneRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return toSerializable(value as Record<string, unknown>);
}

export function hasRenderableContent(layoutJson: Record<string, unknown> | null | undefined): boolean {
  return Array.isArray(layoutJson?.['pageContent']);
}

export function normalizeDashboardName(name: string): string {
  return String(name || '').trim().toLowerCase();
}

export function dashboardSlug(name: string): string {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Stable local id of a saved dashboard. */
export function createDashboardStableId(): string {
  return `dvm-dashboard-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function buildDashboardId(name: string, stableId: string): string {
  const namePart = dashboardSlug(name) || 'untitled-dashboard';
  const suffix = stableId.split('-').pop() || stableId;
  return `${namePart}-${suffix}`;
}

export function extractHttpErrorDetail(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    const bodyMessage =
      (typeof error.error === 'string' && error.error) ||
      error.error?.message ||
      error.error?.detail ||
      error.error?.error ||
      '';
    return [error.status ? `${error.status}` : '', bodyMessage || error.statusText || error.message]
      .filter(Boolean)
      .join(' ');
  }
  return error instanceof Error ? error.message : '';
}

/** The `dashboard_layouts` insert / update envelope of the publish API. */
export function buildPublishEnvelope(layoutJson: Record<string, unknown>, emailId: string, dashboardId: string, uuid: string | null = null): Record<string, unknown> {
  const operation = uuid ? 'update' : 'insert';
  return {
    operation,
    requestData: {
      email_id: emailId,
      role: 'User',

      layout_json: layoutJson,
      dashboard_id: dashboardId,
      ...(uuid ? { uuid } : {}),
      operation,
    },
    responseList: [],
    entity: 'dashboard_layouts',
    batchId: 'test',
    workflowId: 'ABC-123',
    endpoints: [],
    version: '1.0',
  };
}

/** File Upload and Popup tiles take no card-header formatting. */
export function isFormattableTile(tile: DashboardTile): boolean {
  return (
    !!tile &&
    tile.layoutType !== 'FILE_UPLOAD' &&
    tile.layoutType !== 'POPUP'
  );
}

export function tileTakesHeaderFormat(tile: DashboardTile): boolean {
  return isFormattableTile(tile) || tile?.layoutType === 'POPUP';
}

// ── table tiles: served rows ────────────────────────────────────────────────

/** A restored flat table keeps its served rows as `staticData`; drill tables drop them (re-fetched on open). */
export function restoredTableMatrixData(renderData: Record<string, unknown>): Record<string, unknown> {
  const { data, ...rest } = renderData;
  const served = (data as { data?: unknown } | undefined)?.data;
  const caps = (rest['capabilities'] as Record<string, unknown> | undefined) ?? {};
  const isDrill = caps['tableType'] === 'drilldown' || (caps['typeFlags'] as Record<string, unknown> | undefined)?.['drilldown'] === true;
  if (isDrill || !Array.isArray(served) || !served.length) return rest;
  return { ...rest, staticData: served };
}

/** Publishing never carries the served `data` block of a table tile. */
export function publishableTableMatrixData(data: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!data || !('data' in data)) return data;
  const { data: _served, ...rest } = data;
  void _served;
  return rest;
}

// ── restore: widget → tile ──────────────────────────────────────────────────

export interface TileRestoreContext {
  /** Reuse a saved tile id (and keep the id counter ahead of it), or mint a new one. */
  restoreTileId(value: unknown): string;
  createComponentId(prefix?: string): string;
  defaultTileFormat(layoutType: string): Record<string, unknown>;
}

export function tileFromWidget(widget: Record<string, unknown>, ctx: TileRestoreContext): DashboardTile {
  if (String(widget['type']) === 'group-card') return groupTileFromWidget(widget, ctx);

  const layoutType = WIDGET_TYPE_TO_LAYOUT[String(widget['type'])] || 'WATERFALL';
  const wLayout = (widget['layout'] as { colSpan?: number; rowSpan?: number }) || {};
  const payload = (widget['payload'] as Record<string, unknown>) || {};
  const { type, title, widgetId, componentId, layout: _l, payload: _p, format: savedFormat, height: _h, class: _c, recomputeUrl: _r, apiConfig: widgetApiConfig, filterKey: savedFilterKey, listenerTileIds: savedListeners, ...renderData } =
    widget as Record<string, unknown>;
  void type; void widgetId; void _l; void _p; void _h; void _c; void _r;
  const savedApiConfig = restoreApiCallConfig(payload['apiConfig'] ?? widgetApiConfig);
  const tile: DashboardTile = {
    id: ctx.restoreTileId(widgetId),
    componentId: String(componentId ?? ctx.createComponentId()),
    layoutType,
    title: String(title ?? 'Untitled'),
    colSpan: clampNumber(Math.round(wLayout.colSpan ?? 6), 2, DASH_COLS),
    rowSpan: Math.max(3, Math.round(wLayout.rowSpan ?? 8)),
    payload,
    ...(typeof savedFilterKey === 'string' && savedFilterKey ? { filterKey: savedFilterKey } : {}),
    ...(Array.isArray(savedListeners) ? { listenerTileIds: (savedListeners as unknown[]).map(String) } : {}),
  };
  if (isFormattableTile(tile)) {
    tile.format = (savedFormat as Record<string, unknown>) ?? ctx.defaultTileFormat(layoutType);
  }
  if (layoutType === 'DRILL_DOWN_TABLE') tile.drillData = renderData;
  else if (layoutType === 'TABLE_MATRIX') tile.tableMatrixData = restoredTableMatrixData(renderData);
  else if (layoutType === 'HORIZONTAL_BAR_CHART') tile.hbData = renderData;
  else if (layoutType === 'KPI') tile.kpiData = renderData;
  else if (layoutType === 'BAR') {
    tile.barData = renderData;
    const staticRows = renderData['staticData'];
    tile.barRows = Array.isArray(staticRows) ? (staticRows as Array<Record<string, unknown>>) : [];
  }
  else if (layoutType === 'LINE_AREA') {
    tile.lineAreaData = renderData;
    const staticRows = renderData['staticData'];
    tile.lineAreaRows = Array.isArray(staticRows) ? (staticRows as Array<Record<string, unknown>>) : [];
  }
  else if (layoutType === 'SCATTER_BUBBLE') {
    tile.scatterBubbleData = renderData;
    const staticRows = renderData['staticData'];
    tile.scatterBubbleRows = Array.isArray(staticRows) ? (staticRows as Array<Record<string, unknown>>) : [];
  }
  else if (layoutType === 'WATERFALL') {
    tile.waterfallBars = (renderData['waterfallBars'] as WaterfallBar[] | undefined) ?? undefined;
    tile.previewOption = (renderData['option'] as echarts.EChartsOption) ?? null;
    tile.waterfallData = buildWaterfallTileData(tile);
  }
  else if (layoutType === 'SLIDER' || layoutType === 'INPUT_BOX') {
    tile.inputControl = {
      kind: layoutType,
      formula: String(payload['formula'] ?? ''),
      value: (payload['value'] as number | string | null) ?? null,
      targets: Array.isArray(payload['targets'])
        ? (payload['targets'] as Array<{ componentId?: string }>).map((t) => t.componentId || '').filter(Boolean)
        : [],
      widgetData: renderData,
      ...(savedApiConfig ? { apiConfig: savedApiConfig } : {}),
    };
  } else if (layoutType === 'DROPDOWN') {
    const optionOrderMode = normalizeDropdownOptionOrderMode(payload['optionOrderMode'] ?? renderData['optionOrderMode']);
    const customOptionOrder = String(payload['customOptionOrder'] ?? renderData['customOptionOrder'] ?? '');
    const rawOptions = Array.isArray(renderData['optionsList']) ? (renderData['optionsList'] as unknown[]).map(String) : [];
    const options = orderDropdownOptions(rawOptions, optionOrderMode, customOptionOrder);
    const rawValue = payload['value'];
    tile.dropdownControl = {
      field: String(payload['field'] ?? renderData['key'] ?? ''),
      options,
      value: Array.isArray(rawValue) ? rawValue.map(String) : rawValue == null || rawValue === '' ? [] : [String(rawValue)],
      targets: Array.isArray(payload['targets'])
        ? (payload['targets'] as Array<{ componentId?: string }>).map((target) => target.componentId || '').filter(Boolean)
        : [],
      widgetData: {
        ...renderData,
        optionsList: options,
        optionOrderMode,
        customOptionOrder,
        displayMode: payload['displayMode'] ?? renderData['displayMode'] ?? 'dropdown',
        optionGroups: Array.isArray(payload['optionGroups'])
          ? payload['optionGroups']
          : (renderData['optionGroups'] ?? []),
        showAllOption: payload['showAllOption'] ?? renderData['showAllOption'] ?? false,
        name: renderData['name'] ?? payload['displayLabel'] ?? payload['title'] ?? payload['field'] ?? '',
      },
      ...(savedApiConfig ? { apiConfig: savedApiConfig } : {}),
    };
  } else if (layoutType === 'FILE_UPLOAD') {
    const targets = Array.isArray(payload['targets'])
      ? (payload['targets'] as Array<{ componentId?: string }>).map((t) => t.componentId || '').filter(Boolean)
      : [];
    tile.fileUploadControl = {
      targets,
      buttonLabel: String(payload['buttonLabel'] ?? renderData['buttonLabel'] ?? 'Upload file'),
      accept: String(payload['accept'] ?? renderData['accept'] ?? '.xlsx,.xls,.csv'),
      widgetData: renderData,
      ...(savedApiConfig ? { apiConfig: savedApiConfig } : {}),
    };
  } else if (layoutType === 'POPUP') {
    tile.popupControl = { widgetData: { ...renderData, title: String(title ?? '') } };
    if (savedFormat) tile.format = savedFormat as Record<string, unknown>;
  }
  return tile;
}

export function groupTileFromWidget(widget: Record<string, unknown>, ctx: TileRestoreContext): DashboardTile {
  const wLayout = (widget['layout'] as { colSpan?: number; rowSpan?: number }) || {};
  const children = Array.isArray(widget['children']) ? widget['children'] as Array<Record<string, unknown>> : [];
  return {
    id: ctx.restoreTileId(widget['widgetId']),
    componentId: String(widget['componentId'] ?? ctx.createComponentId('GRP')),
    layoutType: 'GROUP',
    title: String(widget['title'] ?? 'Grouped card'),
    groupTitle: String(widget['title'] ?? 'Grouped card'),
    colSpan: clampNumber(Math.round(wLayout.colSpan ?? 6), 2, DASH_COLS),
    rowSpan: Math.max(3, Math.round(wLayout.rowSpan ?? 8)),
    payload: {},
    format: (widget['format'] as Record<string, unknown> | undefined) ?? ctx.defaultTileFormat('GROUP'),
    groupColumns: clampNumber(Math.round(Number(widget['groupColumns']) || 1), 1, 2),
    groupMembers: children.map((child) => tileFromWidget(child, ctx)),
  };
}

// ── grid placement (layout.items / breakpoints.xl) ──────────────────────────

/** Items of a saved `layout` block (breakpoint `xl` wins over `items`), each carrying its componentId. */
export function readPublishedLayoutItems(layout: Record<string, unknown> | null): Array<Record<string, unknown>> {
  const savedLayout = cloneRecord(layout);
  if (!savedLayout) return [];

  const layoutItems = Array.isArray(savedLayout['items']) ? savedLayout['items'] as Array<Record<string, unknown>> : [];
  const breakpoints = savedLayout['breakpoints'] as Record<string, unknown> | undefined;
  const breakpointItems = Array.isArray(breakpoints?.['xl']) ? breakpoints['xl'] as Array<Record<string, unknown>> : [];
  const rawItems = breakpointItems.length ? breakpointItems : layoutItems;
  if (!rawItems.length) return [];

  const componentIdByLayoutId = new Map<string, string>();
  layoutItems.forEach((item) => {
    const id = String(item['id'] ?? '').trim();
    const componentId = String(item['componentId'] ?? '').trim();
    if (id && componentId) componentIdByLayoutId.set(id, componentId);
  });

  return rawItems.map((item) => {
    const id = String(item['id'] ?? '').trim();
    return { ...item, componentId: item['componentId'] ?? componentIdByLayoutId.get(id) ?? '' };
  });
}

/** Re-apply a saved grid placement (span / position / order) to restored tiles; unplaced tiles keep their order at the end. */
export function applyPublishedLayoutToTiles(tiles: DashboardTile[], layout: Record<string, unknown> | null): DashboardTile[] {
  const rawItems = readPublishedLayoutItems(layout);
  if (!rawItems.length) return tiles;

  const tileById = new Map(tiles.map((tile) => [tile.id, tile]));
  const tileByComponentId = new Map(tiles.map((tile) => [tile.componentId, tile]));
  const seen = new Set<string>();
  const ordered: Array<{ order: number; index: number; tile: DashboardTile }> = [];

  rawItems.forEach((item, index) => {
    const id = String(item['id'] ?? '').trim();
    const componentId = String(item['componentId'] ?? '').trim();
    const tile = (id ? tileById.get(id) : undefined) ?? (componentId ? tileByComponentId.get(componentId) : undefined);
    if (!tile || seen.has(tile.id)) return;
    seen.add(tile.id);

    const colSpan = toFiniteNumber(item['colSpan']);
    const rowSpan = toFiniteNumber(item['rowSpan']);
    const x = toFiniteNumber(item['x']);
    const y = toFiniteNumber(item['y']);
    const order = toFiniteNumber(item['order']);
    const nextColSpan = colSpan === null ? tile.colSpan : clampNumber(Math.round(colSpan), 2, DASH_COLS);
    ordered.push({
      order: order ?? index,
      index,
      tile: {
        ...tile,
        colSpan: nextColSpan,
        rowSpan: rowSpan === null ? tile.rowSpan : Math.max(3, Math.round(rowSpan)),
        x: x === null ? tile.x : clampNumber(Math.round(x), 0, DASH_COLS - nextColSpan),
        y: y === null ? tile.y : Math.max(0, Math.round(y)),
        customSize: item['customSize'] === true || tile.customSize === true,
      },
    });
  });

  ordered.sort((a, b) => a.order - b.order || a.index - b.index);
  return [
    ...ordered.map((item) => item.tile),
    ...tiles.filter((tile) => !seen.has(tile.id)),
  ];
}

export function layoutItemForTile(tile: DashboardTile, order: number, restored?: Record<string, unknown>): PublishedLayoutSnapshotItem {
  if (restored) {
    const colSpan = clampNumber(Math.round(toFiniteNumber(restored['colSpan']) ?? toFiniteNumber(tile.colSpan) ?? 6), 1, DASH_COLS);
    const restoredX = toFiniteNumber(restored['x']);
    const restoredY = toFiniteNumber(restored['y']);
    return {
      id: tile.id,
      componentId: tile.componentId,
      colSpan,
      rowSpan: Math.max(1, Math.round(toFiniteNumber(restored['rowSpan']) ?? toFiniteNumber(tile.rowSpan) ?? 8)),
      x: restoredX === null ? -1 : Math.max(0, Math.round(restoredX)),
      y: restoredY === null ? -1 : Math.max(0, Math.round(restoredY)),
      order: toFiniteNumber(restored['order']) ?? order,
      customSize: restored['customSize'] === true,
    };
  }
  return {
    id: tile.id,
    componentId: tile.componentId,
    colSpan: clampNumber(Math.round(toFiniteNumber(tile.colSpan) ?? 6), 1, DASH_COLS),
    rowSpan: Math.max(1, Math.round(toFiniteNumber(tile.rowSpan) ?? 8)),
    x: -1,
    y: -1,
    order,
    customSize: tile.customSize === true,
  };
}

/** Flow every unplaced item (x/y = -1) left-to-right below the placed ones. */
export function placeMissingLayoutItems(items: PublishedLayoutSnapshotItem[]): PublishedLayoutSnapshotItem[] {
  let cursorX = 0;
  let cursorY = items.reduce((bottom, item) => item.x >= 0 && item.y >= 0 ? Math.max(bottom, item.y + item.rowSpan) : bottom, 0);
  let rowHeight = 0;

  return items.map((item) => {
    if (item.x >= 0 && item.y >= 0) return item;
    if (cursorX + item.colSpan > DASH_COLS) {
      cursorX = 0;
      cursorY += rowHeight;
      rowHeight = 0;
    }
    const placed = { ...item, x: cursorX, y: cursorY };
    cursorX += item.colSpan;
    rowHeight = Math.max(rowHeight, item.rowSpan);
    return placed;
  });
}

/** Grid items for the current tiles, seeded from the restored layout where the tile existed before. */
export function buildPublishedLayoutItems(tiles: DashboardTile[], restoredLayout: Record<string, unknown> | null): PublishedLayoutSnapshotItem[] {
  const restoredItems = readPublishedLayoutItems(restoredLayout);
  const restoredById = new Map(restoredItems.map((item) => [String(item['id'] ?? '').trim(), item]));
  const restoredByComponentId = new Map(restoredItems.map((item) => [String(item['componentId'] ?? '').trim(), item]));
  const seeds = tiles.map((tile, index) => {
    const restored = restoredById.get(tile.id) ?? restoredByComponentId.get(tile.componentId);
    return layoutItemForTile(tile, index, restored);
  });
  return placeMissingLayoutItems(seeds);
}

export function buildPublishedLayoutSnapshot(restoredLayout: Record<string, unknown> | null, items: PublishedLayoutSnapshotItem[]): Record<string, unknown> {
  const existing = cloneRecord(restoredLayout) ?? {};
  return {
    ...existing,
    columns: Number(existing['columns'] ?? DASH_COLS),
    items,
    breakpoints: {
      ...((existing['breakpoints'] as Record<string, unknown> | undefined) ?? {}),
      xl: items.map(({ id, colSpan, rowSpan, x, y, order, customSize }) => ({ id, colSpan, rowSpan, x, y, order, customSize })),
    },
  };
}

// ── export: tile → widget → layout_json ─────────────────────────────────────

export interface TileWidgetContext {
  /** `publishedView.publishStaticData` — keep baked rows in the published JSON (default false). */
  publishStaticData: boolean;
  /** Compute endpoint written into input / waterfall widgets for the published view. */
  recomputeUrl: string;
  /** Every top-level tile (for listener resolution). */
  allTiles: DashboardTile[];
  previewTileId: string | null;
}

export interface TileWidgetPlacement {
  span: number;
  rowSpan: number;
  x: number;
  y: number;
  order: number;
  customSize: boolean;
}

export function buildTileWidget(tile: DashboardTile, placement: TileWidgetPlacement, ctx: TileWidgetContext): Record<string, unknown> {
  const { span, rowSpan, x, y, order, customSize } = placement;
  const renderData: Record<string, unknown> =
    (tile.drillData as Record<string, unknown>) ??
    publishableTableMatrixData(tile.tableMatrixData as Record<string, unknown> | undefined) ??
    (tile.hbData as Record<string, unknown>) ??
    (tile.kpiData as Record<string, unknown>) ??
    (tile.barData as Record<string, unknown>) ??
    (tile.lineAreaData as Record<string, unknown>) ??
    (tile.scatterBubbleData as Record<string, unknown>) ??
    (tile.inputControl?.widgetData as Record<string, unknown>) ??
    (tile.dropdownControl?.widgetData as Record<string, unknown>) ??
    (tile.fileUploadControl?.widgetData as Record<string, unknown>) ??
    (tile.popupControl?.widgetData as Record<string, unknown>) ??
    (tile.waterfallBars
      ? { waterfallBars: tile.waterfallBars, option: tile.previewOption }
      : (tile.previewOption ? { option: tile.previewOption } : {}));
  const isInputControl = tile.layoutType === 'SLIDER' || tile.layoutType === 'INPUT_BOX' || tile.layoutType === 'DROPDOWN' || tile.layoutType === 'FILE_UPLOAD';
  return stripWidgetData({
    ...renderData,
    type: LAYOUT_TYPE_TO_WIDGET[tile.layoutType] || 'bar-chart',
    title: tile.title,
    widgetId: tile.id,
    componentId: tile.componentId,
    class: `col-${span}`,
    layout: { colSpan: span, rowSpan, x, y, order, customSize },
    height: Math.max(rowSpan, 1) * DASH_ROW_PX,
    payload: tile.payload,
    ...(tile.format ? { format: tile.format } : {}),
    ...(isCrossFilterTile(tile) || isRecomputeCrossFilterTile(tile)
      ? {
          filterKey: effectiveTileFilterKey(tile),
          listenerTileIds: effectiveListenerTileIds(tile, ctx.allTiles, ctx.previewTileId),
        }
      : {}),
    ...(isRecomputeCrossFilterTile(tile)
      ? {
          crossFilter: tile.format?.['crossFilter'] === true,
          highlightOnClick: tile.format?.['highlightOnClick'] === true,
          recomputeUrl: ctx.recomputeUrl,
        }
      : {}),
    ...(isInputControl
      ? {
          recomputeUrl: ctx.recomputeUrl,
          // The editor writes a changed config into the tile's payload; the control's own copy is only
          // ever populated when a layout is LOADED, so it goes stale the moment the config is edited.
          apiConfig: (tile.payload as Record<string, unknown> | undefined)?.['apiConfig']
            ?? tile.dropdownControl?.apiConfig
            ?? tile.inputControl?.apiConfig
            ?? tile.fileUploadControl?.apiConfig,
        }
      : {}),
  }, ctx.publishStaticData);
}

export function buildGroupCardWidget(tile: DashboardTile, tileLayout: PublishedLayoutSnapshotItem, ctx: TileWidgetContext): Record<string, unknown> {
  const members = tile.groupMembers || [];
  let cursorY = 0;
  const children = members.map((member, memberIndex) => {
    const memberSpan = clampNumber(member.colSpan, 1, tileLayout.colSpan);
    const child = buildTileWidget(member, { span: memberSpan, rowSpan: member.rowSpan, x: 0, y: cursorY, order: memberIndex, customSize: false }, ctx);
    cursorY += member.rowSpan;
    return child;
  });
  return {
    type: 'group-card',
    title: tile.groupTitle || tile.title || 'Grouped card',
    widgetId: tile.id,
    componentId: tile.componentId,
    ...(tile.format ? { format: tile.format } : {}),
    class: `col-${tileLayout.colSpan}`,
    layout: { colSpan: tileLayout.colSpan, rowSpan: tileLayout.rowSpan, x: tileLayout.x, y: tileLayout.y, order: tileLayout.order, customSize: tileLayout.customSize },
    height: Math.max(tileLayout.rowSpan, 1) * DASH_ROW_PX,
    innerColumns: tileLayout.colSpan,
    groupColumns: tile.groupColumns || 1,
    children,
  };
}

export interface DashboardLayoutJsonInput {
  tiles: DashboardTile[];
  /** The live preview draft is never published. */
  previewTileId: string | null;
  dashboardName: string;
  restoredPublishedLayout: Record<string, unknown> | null;
  status: 'draft' | 'published';
}

/** The whole `layout_json`: name / flags / grid snapshot / `pageContent` rows packed to 12 columns. */
export function buildDashboardLayoutJson(input: DashboardLayoutJsonInput, ctx: Omit<TileWidgetContext, 'allTiles' | 'previewTileId'>): Record<string, unknown> {
  const columns = DASH_COLS;
  const widgetCtx: TileWidgetContext = { ...ctx, allTiles: input.tiles, previewTileId: input.previewTileId };
  const layoutItems = buildPublishedLayoutItems(input.tiles, input.restoredPublishedLayout);
  const layoutById = new Map(layoutItems.map((item) => [item.id, item]));

  const pageContent: Record<string, unknown>[] = [];
  let buffer: Record<string, unknown>[] = [];
  let used = 0;
  input.tiles.forEach((tile, index) => {
    if (tile.id === input.previewTileId) return;
    const tileLayout = layoutById.get(tile.id) ?? layoutItemForTile(tile, index);
    const span = Math.min(tileLayout.colSpan, columns);
    const widget = tile.layoutType === 'GROUP' && tile.groupMembers?.length
      ? buildGroupCardWidget(tile, tileLayout, widgetCtx)
      : buildTileWidget(tile, { span, rowSpan: tileLayout.rowSpan, x: tileLayout.x, y: tileLayout.y, order: tileLayout.order, customSize: tileLayout.customSize }, widgetCtx);
    if (used + span > columns && buffer.length) {
      pageContent.push({ id: `row${pageContent.length + 1}`, columns: buffer });
      buffer = [];
      used = 0;
    }
    buffer.push(widget);
    used += span;
  });
  if (buffer.length) pageContent.push({ id: `row${pageContent.length + 1}`, columns: buffer });

  const name = input.dashboardName.trim() || 'Untitled dashboard';
  const now = new Date().toISOString();
  return toSerializable({
    'dashboard-name': name,
    name,
    builder: 'dvm-layout',
    screenFloating: true,
    enableEdit: true,
    layout: buildPublishedLayoutSnapshot(input.restoredPublishedLayout, layoutItems),
    status: input.status,
    updatedAt: now,
    ...(input.status === 'published' ? { publishedAt: now } : {}),
    pageContent,
  });
}
